import React, { useEffect, useState } from "react";
import { Grid, GridColumn as Column, GridDataStateChangeEvent } from "@progress/kendo-react-grid";
import { process, SortDescriptor, State } from "@progress/kendo-data-query";
import { Card, Typography, Box } from "@mui/material";
import { useWorklistQuery } from "../../../../services/verifyService/verify-api";
import { WorklistRequest } from "../../../../services/verifyService/commands";
import { ColumnMenu } from "../../../../utils/ColumnMenu";
import { UserAssignmentViewCell } from "./UserAssignmentViewCell";
import { getProcessType } from "./utils/getProcessType";
import { useUserWorkloadMutation } from "../../../../services/profileService/profile-api";
import { UserWorkloadItem } from "../../../../services/profileService/commands";

type UserAssignmentSummaryProps = {
    worklistType: string;
    center: string;
    filter: string;
};

interface UserAssignmentRow {
    userName: string;
    upn: string;
    assignmentTypes: string[];
    processTypes: string[];
    totalCases: number;
}

function UserAssignmentSummary({ worklistType, center, filter }: UserAssignmentSummaryProps) {
    const { data: worklistData, isLoading: worklistLoading } = useWorklistQuery(
        new WorklistRequest(worklistType, center, filter)
    );
    const [calculateUserWorkload, { isLoading: workloadLoading }] = useUserWorkloadMutation();
    const [userAssignmentData, setUserAssignmentData] = useState<UserAssignmentRow[]>([]);

    const initialSort: SortDescriptor[] = [{ field: 'totalCases', dir: 'desc' }];
    const defaultGridState: State = { sort: initialSort };
    const [dataState, setDataState] = useState(defaultGridState);

    const dataStateChange = (event: GridDataStateChangeEvent) => {
        setDataState(event.dataState);
    };

    // Fetch user workload from backend when worklist data is available
    useEffect(() => {
        if (!worklistData || worklistData.length === 0) {
            setUserAssignmentData([]);
            return;
        }

        const processType = getProcessType(worklistType);

        // Map worklist data to request format
        const worklistItems = worklistData.map(item => ({
            account: item.account,
            calcType: item.calcType,
            centerId: item.centerId,
            isOutsourced: item.isOutsourced
        }));

        calculateUserWorkload({
            worklistItems,
            processType,
            worklistType
        }).then(result => {
            if ('data' in result && result.data) {
                const rows: UserAssignmentRow[] = result.data.users.map((user: UserWorkloadItem) => ({
                    userName: user.userName,
                    upn: user.upn,
                    assignmentTypes: user.assignmentTypes,
                    processTypes: [processType],
                    totalCases: user.totalCases
                }));
                setUserAssignmentData(rows);
            }
        }).catch(error => {
            console.error('Error calculating user workload:', error);
            setUserAssignmentData([]);
        });
    }, [worklistData, worklistType, calculateUserWorkload]);

    if (worklistLoading || workloadLoading) {
        return <Typography>Loading user assignments...</Typography>;
    }

    return (
        <Card sx={{ padding: '16px', borderRadius: '10px' }}>
            <Box sx={{ borderBottom: '3px solid gray', paddingBottom: '8px', marginBottom: '16px' }}>
                <Typography variant="h6" className="psn-green">User Assignments</Typography>
            </Box>
            <Grid
                data={process(userAssignmentData, dataState)}
                style={{ height: '750px' }}
                sortable={{ mode: 'multiple' }}
                onDataStateChange={dataStateChange}
            >
                <Column cell={UserAssignmentViewCell} width="95px" />
                <Column field="userName" title="User" columnMenu={ColumnMenu} />
                <Column field="totalCases" title="Total Cases" width="150px" columnMenu={ColumnMenu} />
            </Grid>
        </Card>
    );
}

export { UserAssignmentSummary };
